<template>
	<div class="row-collapsable">
		<header
			class="row-collapsable__header"
			data-testid="row-collapsable-header"
		>
			<slot name="rowCollapsableHeader">
				<!-- Optional Head Slot -->
			</slot>
		</header>
		<!--        Note for Dev:
        i :style="{ '--header-height': `${headerHeight}px` }"
        I added this, to calculate the height of the table header and thus know how many pixels
        I have to move the scroll, down.... please check the drawbacks that this may have -->
		<div
			ref="tableContainer"
			:class="[
				'row-collapsable__table',
				hasTotalRow && 'row-collapsable__total-row'
			]"
			:style="{
				'--header-height': `${headerHeight}px`
			}"
		>
			<ul
				class="row-collapsable__body"
				data-testid="row-collapsable-data"
				:style="{
					...(minHeight && { 'min-height': `${minHeight}px` })
				}"
				v-maxHeight="offset"
			>
				<li
					class="row-collapsable__tr row-collapsable__thead"
					ref="tableHeader"
				>
					<ButtonIcon
						v-if="allowExpand"
						iconClassName="icon-arrow_right_filled"
						@click="onExpandAllClick()"
						:class="[
							'row-collapsable__icon-expand-collapse row-collapsable__icon-header',
							isOpenClass
						]"
						variant="rotate"
						data-testid="row-collapsable-allowExpand"
						:disabled="showNoResults"
					/>
					<div
						class="row-collapsable__checkbox-container"
						v-show="hasCheckbox"
					>
						<Checkbox
							class="row-collapsable__checkbox"
							data-id="row-collapsable-select-all"
							@change="onSelectAll"
							:value="isCheckedSelectAll || this.allSelected"
							:indeterminate="checkboxIndeterminate"
							ref="selectAllCheckbox"
							:disabled="showNoResults || this.allDisabled"
						/>
					</div>
					<div class="row-collapsable__data-container">
						<li
							:class="[
								'row-collapsable__th',
								column.columnModifierName !== undefined &&
									'row-collapsable__th--' +
										column.columnModifierName,
								!column.isSortable && 'no-sortable'
							]"
							v-for="(column, index) in displayColumns"
							:key="column.columnName"
							@click="
								column.isSortable
									? handleSort(
											ascending,
											column.columnName,
											index,
											column.sortDirection,
											column.isSortable
									  )
									: null
							"
							tabindex="0"
						>
							<div class="row-collapsable__data-header">
								<div class="row-collapsable__display-name">
									<span
										:class="[
											'row-collapsable__display-name--' +
												column.columnModifierName
										]"
									>
										{{ column.columnDisplayName }}
									</span>
									<div
										class="row-collapsable__sort-action"
										v-if="column.isSortable"
									>
										<i
											:class="[
												'icon',
												sortDirectionClasses(
													column.sortDirection
												)
											]"
											data-testid="row-collapsable-isSorteable"
										></i>
									</div>
								</div>
							</div>
						</li>
					</div>
				</li>
				<li
					class="row-collapsable__tr"
					v-for="(record, index) in internalRecordset"
					:key="record.$id"
				>
					<div class="row-collapsable__data-group">
						<div class="row-collapsable__tr">
							<ButtonIcon
								v-if="allowExpand"
								iconClassName="icon-arrow_right_filled"
								@click="onExpandClick(record)"
								:class="[
									'row-collapsable__icon-expand-collapse',
									itemsExpanded[record.$id] && 'is-open'
								]"
								variant="rotate"
								data-testid="row-collapsable-expand"
							/>
							<div
								class="row-collapsable__checkbox-container"
								@mouseenter="
									onCheckboxMouseEnter(
										record,
										`singleSelectCheckbox${record[idColumn]}`
									)
								"
								@mouseleave="
									onCheckboxMouseLeave(
										record,
										`singleSelectCheckbox${record[idColumn]}`
									)
								"
								v-show="hasCheckbox"
							>
								<Checkbox
									class="row-collapsable__checkbox"
									data-id="row-collapsable-single-select"
									:value="record.selected"
									:disabled="record.disabled"
									@change="
										value => {
											onSelect(value, record[idColumn]);
										}
									"
									:ref="
										`singleSelectCheckbox${record[idColumn]}`
									"
								/>
							</div>
							<slot
								name="checkboxContent"
								v-bind:record="record"
							></slot>
							<div class="row-collapsable__data-container">
								<div
									class="row-collapsable__td"
									:class="
										'row-collapsable__td--' +
											column.columnModifierName
									"
									v-for="column in displayColumns"
									:key="column.columnName"
								>
									<slot
										:name="column.slot"
										v-bind:record="internalRecordset[index]"
									>
										<component
											:is="
												column.component
													? column.component.name
													: 'span'
											"
											v-bind="
												column.component &&
												column.component.props
													? column.component.props(
															record,
															column,
															dynamicComponentEventHandler
													  )
													: {
															class: [
																'row-collapsable__data',
																'row-collapsable__data--' +
																	column.columnModifierName
															]
													  }
											"
											v-on="
												column.component &&
												column.component.events
													? column.component.events(
															record,
															column,
															dynamicComponentEventHandler
													  )
													: {}
											"
											>{{
												record[column.columnName]
											}}</component
										>
									</slot>
								</div>
							</div>
						</div>
						<div
							class="row-collapsable__nested"
							data-testid="nested-slot"
							v-if="
								record[childRecordsetPropName] &&
									itemsExpanded[record.$id]
							"
						>
							<slot
								name="nestedRowHeader"
								v-bind:records="record"
							></slot>
							<slot
								name="nestedRow"
								v-bind:records="record[childRecordsetPropName]"
							>
								{{ record[childRecordsetPropName] }}
							</slot>
						</div>
					</div>
				</li>
				<li v-if="showNoResults">
					<NoResultsMessage
						:visible="true"
						:primaryTextCondensed="noResultsPrimaryText"
						:secondaryTextCondensed="noResultsSecondaryText"
					/>
				</li>
			</ul>
		</div>
		<footer
			class="row-collapsable__footer"
			v-if="hasTotalRow"
			data-testid="row-collapsable-footer"
		>
			<slot name="rowCollapsableFooter">
				<!-- Footer Content -->
			</slot>
		</footer>
	</div>
</template>

<script>
import tableMixin from '@/mixins/table-mixin';
import Checkbox from '@/components/forms/selection-control/checkbox/checkbox.vue';
import ButtonIcon from '@/components/forms/button/button-icon.vue';
import NumberDisplay from '@/components/visual/number-display/number-display.vue';
import Hyperlink from '@/components/typography/hyperlink/hyperlink.vue';
import NoResultsMessage from '@/components/shells/no-results-message/no-results-message.vue';

export default {
	name: 'RowCollapseTable',
	mixins: [tableMixin],
	components: {
		Checkbox,
		ButtonIcon,
		NumberDisplay,
		Hyperlink,
		NoResultsMessage
	},
	data() {
		return {
			expanded: false,
			itemsExpanded: {}
		};
	},
	props: {
		hasTotalRow: {
			type: Boolean,
			default: false
		},
		allowExpand: {
			type: Boolean,
			default: true
		},
		expandedAll: {
			type: Boolean,
			default: false
		},
		hasCheckbox: {
			type: Boolean,
			default: false
		},
		isCheckedSelectAll: {
			type: Boolean,
			default: false
		},
		childRecordsetPropName: {
			type: String,
			default: 'childRecordset'
		}
	},
	watch: {
		expandedAll: {
			handler(value) {
				if (this.allowExpand && value) {
					this.expandAllRows();
				}
			},
			immediate: true
		}
	},
	computed: {
		allSelected() {
			let result = false;
			let selectedCount = -1;
			let notDisabledCount = -1;

			if (this.internalRecordset && this.internalRecordset.length > 0) {
				selectedCount = this.internalRecordset.filter(
					record => record.selected
				).length;
				notDisabledCount = this.internalRecordset.filter(
					record => !record.disabled
				).length;
			}

			result = notDisabledCount > 0 && selectedCount === notDisabledCount;
			return result;
		},
		allDisabled() {
			let result = false;
			let disabledCount = -1;

			if (this.internalRecordset && this.internalRecordset.length > 0) {
				disabledCount = this.internalRecordset.filter(
					record => record.disabled
				).length;
			}

			result = disabledCount === this.internalRecordset.length;
			return result;
		},
		checkboxIndeterminate() {
			const selected = this.internalRecordset.filter(
				record => record.selected
			);

			return (
				selected.length > 0 &&
				selected.length < this.internalRecordset.filter(record => !record.disabled).length
			);
		},
		isOpenClass() {
			return this.expanded ? 'is-open' : '';
		}
	},
	methods: {
		onExpandClick(record) {
			this.itemsExpanded[record.$id] = !this.itemsExpanded[record.$id];
			this.$forceUpdate();
			this.checkExpanded();
		},
		onExpandAllClick() {
			this.expanded = !this.expanded;

			this.internalRecordset.forEach(item => {
				this.itemsExpanded[item.$id] = this.expanded;
			});
		},
		expandAllRows() {
			this.internalRecordset.forEach(item => {
				this.itemsExpanded[item.$id] = true;
			});
			this.expanded = true;
		},
		onSelect(value, id) {
			const obj = { checked: value.checked };

			obj[this.idColumn] = id;

			this.$emit('onSelect', obj);
		},
		onSelectAll(value) {
			this.$emit('onSelectAll', value);
		},
		onCheckboxMouseEnter(record) {
			this.$emit('checkboxMouseEnter', record);
		},
		onCheckboxMouseLeave(record) {
			this.$emit('checkboxMouseLeave', record);
		},
		checkExpanded() {
			this.expanded = Object.values(this.itemsExpanded).some(value => value === true);
		}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('table');
.row-collapsable {
	display: block;
	position: relative;

	&__data-header {
		display: inline-flex;
		align-items: center;
	}

	&__icon-header {
		margin: $gutter-sm;
		margin-right: 0;
	}

	&__table {
		width: 100%;
		display: block;
		text-align: left;
		position: relative;
		font-size: $txt-font-size;
		background: var(--bg-0);
		color: var(--bg-0-fg-4);
		position: relative;
		border-radius: $border-radius;

		&:deep(.row-collapsable__nested) {
			padding-left: 56px;
			padding-right: $padding-md;
		}
	}

	&__body {
		border: 1px solid var(--neutral-2);
		border-radius: $border-radius;
		display: flex;
		flex-direction: column;
		gap: 0px;
		overflow-y: auto;
		overflow-x: hidden;
	}

	&:deep(.btn-icon) {
		.icon {
			transition: 0.2s transform ease;
		}

		&.is-open {
			.icon {
				transform: rotate(90deg);
			}
		}
	}

	&__checkbox-container {
		align-self: center;
		padding: 0 $padding-sm;
	}

	&:deep(.checkbox__body) {
		margin-left: 0;
	}

	&__icon-expand-collapse {
		align-self: center;
		margin-left: $gutter-sm;
		font-size: 10px;
		min-height: 32px;
	}

	&__tr {
		display: flex;
	}

	&__th {
		display: flex;
		align-items: center;
		background: var(--bg-2);
		color: var(--bg-2-fg-3);
		text-align: left;
		font-weight: $bold-font-weight;
		position: relative;
		height: 100%;
		cursor: pointer;

		&:hover,
		&:focus {
			background-color: var(--brand-2-tint);

			& .is-ascending,
			& .is-descending {
				color: var(--bg-brand-2-tint-fg-2);
			}

			.row-collapsable__sort-action {
				color: var(--bg-brand-2-tint-fg-2);
			}
		}

		&:focus-visible {
			outline: none;
		}
	}

	&__data-container {
		display: flex;
		align-items: flex-start;
		width: 100%;
	}

	&__display-name {
		display: inline-flex;
		align-items: center;
	}

	&__sort-action {
		display: flex;
		align-items: center;
		margin-left: $gutter-md;
		color: var(
			--neutral-3
		); /* is a known exception for deviating from the background/foreground combinations. */
	}

	.is-ascending {
		transform: rotate(180deg);
	}

	.no-sortable {
		cursor: default;

		&:hover {
			background-color: initial;
		}
	}

	&__thead {
		display: flex;
		background: var(--bg-2);
		border-bottom: 1px solid var(--neutral-2);
		position: sticky;
		position: -webkit-sticky;
		top: 0;
		z-index: $z-level1-3;

		& > :last-child {
			border-top-right-radius: $border-radius;
		}
	}

	&__tr:not(:first-child) {
		& + .row-collapsable__tr {
			border-top: 1px solid var(--neutral-2);
		}

		&:nth-child(even) {
			background: var(--bg-1);
			color: var(--bg-1-fg-3);

			&:hover {
				background-color: var(--bg-brand-2-tint);
				color: var(--bg-brand-2-tint-fg-2);
			}
		}

		&:nth-child(odd) {
			color: var(--bg-0-fg-4);

			&:hover {
				background-color: var(--bg-brand-2-tint);
				color: var(--bg-brand-2-tint-fg-2);
			}
		}
	}

	&__td,
	&__th {
		padding: $table-cell-padding-y $table-cell-padding-x;
		flex: 1;
		align-self: flex-start;
	}

	&__td {
		display: flex;
		height: 100%;
		align-items: center;
	}

	&__nested {
		align-self: center;
		padding-top: $padding-md;
		padding-bottom: $padding-md;
		width: 100%;
		background: var(--bg-2);
	}

	&__data-group {
		display: flex;
		flex-direction: column;
		justify-content: center;
		width: 100%;
	}

	&__sort-icon {
		display: flex;
		align-items: center;
	}

	&__total-row {
		::-webkit-scrollbar-track {
			border-bottom-right-radius: 0;
		}

		::-webkit-scrollbar {
			border-bottom-right-radius: 0;
		}

		.row-collapsable__body {
			border-bottom-left-radius: 0;
			border-bottom-right-radius: 0;
		}
	}

	&__footer {
		// Temporary Styles - UI/UX Pending
		width: 100%;
		border: $field-border-width solid var(--neutral-2);
		border-top: none;
		border-bottom-left-radius: $border-radius;
		border-bottom-right-radius: $border-radius;
		background: var(--bg-2);
		padding: $padding-sm;
	}
}

@include mq($from: sm) {
	.table {
		&__icon-expand-collapse {
			min-width: 32px;
		}
	}
}
/* LL styles v1 Only */
.row-collapsable {
	.is-ascending,
	.is-descending {
		color: var(--bg-2-fg-3);
	}
}

/* LL styles v2 Only */
/* .row-collapsable {
    .is-ascending,
    .is-descending {
        color: var(--brand-1);
    }
} */
</style>
